import { Squad } from '../../../types';
import { LevelLite } from '../../curriculum/adapters/types';
import { ResolvedSessionPlan } from './sessionPlan';

export function changeCurriculumFromSession(
  squad: Pick<Squad, 'completedLevels' | 'classStatus' | 'currentLessonId'>,
  plan: ResolvedSessionPlan,
  startSession: number,
  curriculumId: string,
  levels: LevelLite[],
): NonNullable<Squad['sessionPlan']> {
  if (!Number.isInteger(startSession) || startSession < 1 || startSession > plan.slots.length) {
    throw new Error('Choose a session in this class schedule.');
  }
  if (!curriculumId || !levels.length) throw new Error('Choose a curriculum with published lessons.');
  const remaining = plan.slots.length - startSession + 1;
  if (remaining > levels.length) throw new Error(`This curriculum has ${levels.length} lessons, but ${remaining} sessions remain. Choose a later starting session.`);
  const completed = new Set(squad.completedLevels || []);
  if (plan.slots.slice(startSession - 1).some(slot => completed.has(slot.levelId)
    || (squad.classStatus === 'current' && slot.levelId === squad.currentLessonId))) {
    throw new Error('Choose a session after all completed or live classes.');
  }
  return {
    curriculumId: plan.curriculumId,
    slots: plan.slots.map((slot, index) => {
      const source = index < startSession - 1 ? {
        levelId: slot.levelId,
        curriculumId: slot.curriculumId || plan.curriculumId,
        sourceLevelId: slot.sourceLevelId || slot.levelId,
      } : {
        levelId: `session-${index + 1}--${encodeURIComponent(curriculumId)}--${encodeURIComponent(levels[index - startSession + 1].id)}`,
        curriculumId,
        sourceLevelId: levels[index - startSession + 1].id,
      };
      return { index: index + 1, ...source, ...(slot.date ? { date: slot.date } : {}), ...(slot.eventId ? { eventId: slot.eventId } : {}) };
    }),
    updatedAt: Date.now(),
  };
}

/** Re-key source lessons so progress and portfolio records cannot collide between curricula. */
export function assignedLevels(squad: Pick<Squad, 'sessionPlan'>, base: LevelLite[], curricula: Record<string, LevelLite[]>): LevelLite[] {
  if (!squad.sessionPlan?.slots.some(slot => slot.curriculumId)) return base;
  return squad.sessionPlan.slots.flatMap(slot => {
    const source = (curricula[slot.curriculumId || squad.sessionPlan!.curriculumId] || []).find(level => level.id === (slot.sourceLevelId || slot.levelId));
    return source ? [{ ...source, id: slot.levelId }] : [];
  });
}

/** Run against the latest squad inside the save transaction, not an editor snapshot. */
export function assertCurriculumPlanPreservesProgress(squad: Pick<Squad, 'curriculumId' | 'sessionPlan' | 'completedLevels' | 'classStatus' | 'currentLessonId'>, plan: NonNullable<Squad['sessionPlan']>): void {
  if (plan.curriculumId !== (squad.curriculumId || 'music')) throw new Error('The squad curriculum changed. Reload Squad details and try again.');
  const locked = new Set(squad.completedLevels || []);
  if (squad.classStatus === 'current' && squad.currentLessonId) locked.add(squad.currentLessonId);
  for (const levelId of Array.from(locked)) {
    const before = squad.sessionPlan?.slots.find(slot => slot.levelId === levelId);
    const after = plan.slots.find(slot => slot.levelId === levelId);
    if (!after || (before && (before.index !== after.index
      || (before.sourceLevelId || before.levelId) !== (after.sourceLevelId || after.levelId)
      || (before.curriculumId || plan.curriculumId) !== (after.curriculumId || plan.curriculumId)))) {
      throw new Error('A class has been completed or started since this plan was edited. Reload Squad details and try again.');
    }
  }
}
